import { useContext, useEffect, useState } from 'react';
import type {
  NewsView,
  User,
  UserNetworkUpdate,
  UserUpdate,
  WP_DirUser,
  WP_NotifsArray,
  WP_Page,
  WP_Pagination,
  WP_PaginationResult,
  WP_UserNetwork,
} from '../model/models';
import { DirectoryFilterObject, WP_Tag } from '../model/tags/tags';
import { Auth, useAuth } from '../services/AuthService';
import { useLogger } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';
import { WPService, WP_FetchPage } from '../services/WPService';
import { AppContext } from '../state/AppContextProvider';
import { ActionType } from '../state/GlobalState';

export const useWP = () => {
  const { state } = useContext(AppContext);

  const auth = useAuth();

  const ready =
    state.isLoggerReady &&
    state.isConnected &&
    state.user !== undefined &&
    state.locale.length > 0 &&
    state.isOnline;

  const logger = useLogger('useWP');

  useEffect(() => {
    if (ready) {
      logger.debug('ready !!!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  return {
    wp: WPService.getInstance(logger, auth),
    ready: ready,
  };
};

/**
 * Hook useNews()
 * Fetch all news and return it as local sate
 */
export const useNews = () => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const logger = useLogger('useNews');

  const mainHouse = state.mainHouse;

  const [news, setNews] = useState<NewsView[]>([]);

  const requirements = [
    state.user,
    state.locale,
    state.mainHouse,
    state.allHouses,
  ];

  useEffect(() => {
    if (requirements.every((requirement) => requirement)) {
      logger.info(`useEffect() [user,locale,mainHouse,allHouses]`, state.user);
      const mainHouseId = mainHouse?.id || 0;
      wp.getNews(state.locale).then((news) => {
        const newsList = news.map((news) =>
          ModelsService.toNewsView(
            mainHouseId,
            ModelsService.getUserActiveHouse(state.user)?.term_id || 0,
            news,
            state.allHouses,
            state.allServices,
            state.user?.acf.locale
          )
        );
        setNews(newsList);
        dispatch({
          type: ActionType.AddNews,
          payload: newsList,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, requirements);

  return { news: news };
};

export const useNewsByHouse = () => {
  const { state } = useContext(AppContext);

  const { wp } = useWP();

  const mainHouse = state.mainHouse;

  const [houseNews, setHouseNews] = useState<NewsView[]>([]);

  const requirements = [
    state.user,
    state.house,
    state.locale,
    state.mainHouse,
    state.allHouses,
  ];

  useEffect(() => {
    if (requirements.every((requirement) => requirement)) {
      const houseId = state.house?.term_id || 0;
      const mainHouseId = mainHouse?.id || 0;
      if (houseNews.length === 0) {
        wp.getNewsByHouse(houseId, state.locale).then((news) => {
          setHouseNews(
            news.map((news) =>
              ModelsService.toNewsView(
                mainHouseId,
                ModelsService.getUserActiveHouse(state.user)?.term_id || 0,
                news,
                state.allHouses,
                state.allServices,
                state.user?.acf.locale
              )
            )
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, requirements);

  return { houseNews: houseNews };
};

export type UpdateUserFunc = (data: UserUpdate) => Promise<any>;
export type RefreshUserFunc = () => void;

/**
 * Hook useUserInformations()
 * Fetch user and put it in AppContext
 * Provide a 'update' function to update user data.
 * Provide a 'refresh' function to force state to be refreshed.
 */
export const useUserInformations = (
  fetch: boolean = false
): {
  user: User | undefined;
  update: UpdateUserFunc;
  refresh: RefreshUserFunc;
  isLoading: boolean;
} => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const logger = useLogger('useUserInformations');

  const [mustFecthUser, fecthUser] = useState(false);

  useEffect(() => {
    if (state.isAuthLoaded && fetch === true) {
      dispatch({ type: ActionType.SetLoadingOfflineDatas, payload: true });
      logger.info(`Trying to load offline user datas.`);
      Auth.loadOfflineDatas()
        .then((data) => {
          dispatch({
            type: ActionType.SetUser,
            payload: data,
          });
        })
        .catch((err) => {
          logger.info('Unable to load offline user datas:', err);
        })
        .finally(() => {
          dispatch({ type: ActionType.SetLoadingOfflineDatas, payload: false });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthLoaded]);

  useEffect(() => {
    if (
      state.isConnected &&
      // allow component to force a refresh
      (fetch || mustFecthUser)
    ) {
      logger.info(`Loading online user datas...`);
      dispatch({ type: ActionType.SetLoadingOnlineDatas, payload: true });
      fecthUser(false);
      // fetch User Info
      wp.getUserInformations()
        .then((user) => {
          Auth.saveOfflineDatas(user);
          dispatch({
            type: ActionType.SetUser,
            payload: user,
          });
          dispatch({
            type: ActionType.SetOnline,
            payload: true,
          });
        })
        .finally(() => {
          dispatch({
            type: ActionType.SetLoadingOnlineDatas,
            payload: false,
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected, mustFecthUser, state.isAuthLoaded]);

  const refresh = () => {
    fecthUser(true);
  };

  const update = (data: UserUpdate): Promise<User> => {
    // update request
    const userId = +(state?.user?.id || 0);
    return wp.putUserAcf(userId, data).then((data) => {
      refresh();
      return data as User;
    });
  };

  return {
    user: state.user,
    update: update,
    refresh: refresh,
    isLoading: state.isLoadingOfflineDatas || state.isLoadingOnlineDatas,
  };
};

export const useUser = (id: number, fetch?: boolean) => {
  const { wp } = useWP();

  const logger = useLogger();

  const [user, setUser] = useState<WP_DirUser | undefined>(undefined);

  useEffect(() => {
    if (fetch) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchUser = () => {
    logger.info(`fetchUser() with id = ${id}`);
    wp.getUser(id).then((user) => {
      setUser(user);
    });
    logger.info(`fetchUser() DONE with id = ${id}`);
  };

  return { user: user, fetchUser: fetchUser };
};

export const usePagination = <T>(fetch: WP_FetchPage<T>) => {
  const [items, setItems] = useState<Array<T>>([]);

  const [page, setPage] = useState<number>(1);

  const [totalPage, setTotalPage] = useState<number>(1);

  const [hasMore, setHasMore] = useState<boolean>(true);

  const [loading, setLoading] = useState<boolean>(false);

  const [isReset, setIsReset] = useState<boolean>(true);

  const logger = useLogger('usePagination');

  const reset = () => {
    setPage(1);
    setTotalPage(1);
    setHasMore(true);
    setLoading(false);
    setItems([]);
    setIsReset(true);
    logger.info('reset()');
  };

  const fetchMore = (
    per_page: number,
    args
  ): Promise<WP_PaginationResult<T>> => {
    setIsReset(false);
    setLoading(true);
    return new Promise<WP_PaginationResult<T>>((resolve, reject) => {
      if (page <= totalPage) {
        const pagination: WP_Pagination = { per_page: per_page, page: page };
        logger.info(`fetchMore() fetch with pagination:`, pagination);
        fetch(pagination, args).then((r) => {
          setItems(JSON.parse(JSON.stringify(items.concat(r.items))));
          setTotalPage(r.totalPages);
          setPage(page + 1);
          setHasMore(page + 1 > r.totalPages ? false : true);
          resolve(r);
        });
      } else {
        logger.info(`fetchMore() no more page: ${page}/${totalPage}`);
        // no more items
        setHasMore(false);
        reject();
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  return {
    items: items,
    setItems: setItems,
    loading: loading,
    hasMoreItems: hasMore,
    fetchMore: fetchMore,
    reset: reset,
    isReset: isReset,
    page: page,
    totalPage: totalPage,
  };
};

export type CreateUserNetworkFunc = (
  data: UserNetworkUpdate
) => Promise<WP_UserNetwork>;
export type UpdateUserNetworkFunc = (
  userNetworkId: number,
  data: UserNetworkUpdate
) => Promise<any>;
export type FetchUserNetworkFunc = () => Promise<void>;
export type DeleteUserNetworkFunc = (id: number) => Promise<void>;

/**
 * Hook useUserNetwork()
 * Provide a 'update' function to update user network data.
 * Provide a 'refresh' function to force state to be refreshed.
 */
export const useUserNetwork = (
  userId: number | null
): {
  userNetwork: WP_UserNetwork[];
  create: CreateUserNetworkFunc;
  update: UpdateUserNetworkFunc;
  remove: DeleteUserNetworkFunc;
  fetch: FetchUserNetworkFunc;
  loading: boolean;
  getNbFriendRequests: () => number;
  getMyFriendRequests: () => WP_UserNetwork[];
  friends: Array<WP_DirUser>;
  filter: DirectoryFilterObject;
  changeFilter: (filterType: string, value: WP_Tag | null) => void;
  resetFilter: () => void;
  getUserNetwork: (
    user_id_a: number,
    user_id_b: number
  ) => WP_UserNetwork | undefined;
} => {
  const [userNetwork, setUserNetwork] = useState<WP_UserNetwork[]>([]);
  const [friends, setFriends] = useState<Array<WP_DirUser>>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const [filter, setFilter] = useState<DirectoryFilterObject>({});

  const { wp } = useWP();

  const { user } = useUserInformations();

  const logger = useLogger('useUserNetwork');

  const { state } = useContext(AppContext);

  const fetch = () => {
    logger.info('fetching...');
    setLoading(true);
    return wp
      .getUserNetwork()
      .then((userNetworks) => {
        setUserNetwork(userNetworks);
      })
      .finally(() => {
        logger.info('done.');
        setLoading(false);
      });
  };

  const getUserNetwork = (user_id_a: number, user_id_b: number) => {
    if (user_id_a && user_id_b) {
      const found = userNetwork.find(
        (item: WP_UserNetwork) =>
          (item.acf.user_a === user_id_b && item.acf.user_b === user_id_a) ||
          (item.acf.user_a === user_id_a && item.acf.user_b === user_id_b)
      );
      return found;
    } else {
      return undefined;
    }
  };

  const changeFilter = (filterType: string, value: WP_Tag | null) => {
    setFilter({ ...filter, [filterType]: value });
  };

  const resetFilter = () => {
    setFilter({});
  };

  const getFriends = (include: string) => {
    const params = {
      include: include,
    };
    wp.getUsers({}, params).then((users) => {
      setFriends(users.items);
    });
  };

  useEffect(() => {
    if (
      state.isConnected === true &&
      user &&
      Array.isArray(userNetwork) &&
      userNetwork.length > 0
    ) {
      let friendsStringIds = '';
      ModelsService.getMyFriends(userNetwork, user.id).forEach((friend) => {
        if (friendsStringIds === '') {
          friendsStringIds = friendsStringIds + friend.friendId;
        } else {
          friendsStringIds = friendsStringIds + ',' + friend.friendId;
        }
      });
      getFriends(friendsStringIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected, userNetwork, user]);

  const filterMyFriendRequest = (item) =>
    userId !== null &&
    item.acf.relation_status === 'requested' &&
    item.acf.user_b === userId;

  const getNbFriendRequests = () => {
    return userNetwork.filter(filterMyFriendRequest).length;
  };

  const getMyFriendRequests = () => userNetwork.filter(filterMyFriendRequest);

  const create: CreateUserNetworkFunc = (newData: UserNetworkUpdate) => {
    const p = wp.postUserNetwork(newData);
    p.then((data) => {
      fetch();
    });
    return p;
  };

  const update = (userNetworkId: number, data: UserNetworkUpdate) => {
    return wp.putUserNetworkAcf(userNetworkId, data).then((data) => {
      fetch();
    });
  };

  const remove = (userNetworkId: number) => {
    return wp.deleteUserNetwork(userNetworkId).then((data) => {
      fetch();
    });
  };

  return {
    userNetwork: userNetwork,
    create: create,
    update: update,
    remove: remove,
    fetch: fetch,
    loading: loading,
    getNbFriendRequests: getNbFriendRequests,
    getMyFriendRequests: getMyFriendRequests,
    getUserNetwork: getUserNetwork,
    friends: friends,
    changeFilter: changeFilter,
    filter: filter,
    resetFilter: resetFilter,
  };
};

/**
 * Hook usePages()
 * Fetch all pages and return it as local sate
 */
export const usePages = () => {
  const { state } = useContext(AppContext);

  const { wp } = useWP();

  const [pages, setPages] = useState<WP_Page[]>([]);

  useEffect(() => {
    if (state.locale && state.user && pages.length === 0) {
      // Get all Services
      wp.getPages(state.locale).then((p) => {
        setPages(p);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.locale, state.user, pages.length]);

  return { pages: pages };
};

/**
 * Hook useServices()
 * Fetch all services (terms) and put it into AppContext
 */
export const useServiceTerms = (fetch: boolean = false) => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  useEffect(() => {
    if (fetch && state.isConnected) {
      // fetch All Services (terms)
      wp.getServicesTerm().then((services) => {
        dispatch({
          type: ActionType.SetAllServices,
          payload: services,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isConnected]);
};

/**
 * Hook useNotifsByUser
 * Fetch all notifications and return it as local sate
 */
export const useNotifsByUser = () => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const logger = useLogger('useNotifsByUser');

  const fetchNotifs = () => {
    logger.info('fetchNotifs()');
    if (state.user && state.locale && state.mustRefreshNotifs) {
      dispatch({ type: ActionType.SetMustRefreshNotifs, payload: false });
      const idUser = state.user?.username ? state.user.username : false;
      if (idUser !== false) {
        logger.info('getNotifsByUser');
        wp.getNotifsByUser(idUser, state.locale).then(
          (notifs: WP_NotifsArray) => {
            dispatch({ type: ActionType.SetAllNotifs, payload: notifs });
          }
        );
      }
    }
  };

  const mustRefresh = () => {
    logger.info('mustRefresh()');
    dispatch({ type: ActionType.SetMustRefreshNotifs, payload: true });
  };

  /** Init background task, refreshing notifications */
  useEffect(() => {
    const interval = setInterval(mustRefresh, 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  useEffect(() => {
    if (state.mustRefreshNotifs) {
      fetchNotifs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user && state.locale && state.mustRefreshNotifs]);

  return {
    fetchNotifs: mustRefresh,
  };
};

/**
 * Hook useTags
 * @param tagType, slug of tag type to return
 * Fetch all post and return it as local sate
 */
export const useTags = (tagType: string) => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const logger = useLogger(`useTags with ${tagType}`);

  const cacheKey = `parent:${tagType}`;
  const tags = state.cachedTags.get(cacheKey);

  const getParentTag = () => {
    const find = state.parentTags.find((tag) => tag.slug === tagType);
    return find;
  };

  const requirements = [state.user, state.locale, state.parentTags];

  const fetchTags = () => {
    if (requirements.every((requirement) => requirement)) {
      logger.info('fetchTags');
      const parentTag = getParentTag();
      const tax_parent_id = parentTag ? parentTag.id : 0;
      if (tax_parent_id > 0) {
        wp.getTaxChildrenTags(tax_parent_id, 'tax-tag').then(
          (tags: Array<WP_Tag>) => {
            // cache tags
            dispatch({
              type: ActionType.SetCachedTags,
              payload: { key: cacheKey, tags: tags },
            });
          }
        );
      }
    }
  };

  useEffect(() => {
    if (
      requirements.every((requirement) => requirement) &&
      tags === undefined
    ) {
      fetchTags();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...requirements, tags]);

  return { tags: tags || [], fetchTags };
};

/**
 *
 */
export const useUserGroup = (
  tagValue?: Array<string>,
  houseId?: number,
  perPage?: number
) => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const [userGroup, setUserGroup] = useState<Array<WP_DirUser>>([]);
  const [groupTagIds, setGroupTagIds] = useState<Array<number>>([]);
  const [mustFetch, setMustFetch] = useState<boolean>(true);

  const fetchUserGroup = (param?) => {
    wp.getUsers({ per_page: perPage ?? 10 }, param).then((res) => {
      setUserGroup(res.items);
    });
  };

  const arrayToString = (array: Array<string | number>) => {
    let ret: string = '';
    array.forEach((item, index) => {
      if (index !== 0) {
        ret = ret + ',';
      }
      ret = ret + item;
    });

    return ret;
  };

  const buildTagsParams = (tags: WP_Tag[]) => {
    let param = {};
    const tagsId = tags.map((tag) => tag.id);
    setGroupTagIds(tagsId);

    let tagIdsString = arrayToString(tagsId);
    const filterTag = 'filter[tax-tag]';
    param[filterTag] = tagIdsString;

    if (houseId) {
      const filterHouse = 'filter[tax-house]';
      param[filterHouse] = houseId;
    }
    return param;
  };

  const getUserGroup = () => {
    let param = {};
    if (tagValue && tagValue.length > 0) {
      let slug: string = arrayToString(tagValue);

      if (state.cachedTags.has(slug)) {
        const tags = state.cachedTags.get(slug);
        if (tags && tags.length > 0) {
          fetchUserGroup(buildTagsParams(tags));
        }
      } else {
        wp.getTaxTags(slug).then((tags) => {
          if (tags.length > 0) {
            // cache tags
            dispatch({
              type: ActionType.SetCachedTags,
              payload: { key: slug, tags: tags },
            });
            fetchUserGroup(buildTagsParams(tags));
          }
        });
      }
    } else {
      if (houseId) {
        const filterHouse = 'filter[tax-house]';
        param[filterHouse] = houseId;
      }
      fetchUserGroup(param);
    }
  };

  useEffect(() => {
    if (state.user && state.isConnected && mustFetch) {
      setMustFetch(false);
      getUserGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, state.isConnected, userGroup]);

  return {
    userGroup: userGroup,
    groupTagIds: groupTagIds,
  };
};
